'use client';

import { useCallback, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { focusVisibleOutline } from '@/lib/focus-styles';
import type { ProductGiftDetails } from '@/lib/product-gift-schemas';
import { getProductGiftContent, supportsProductGiftOption } from '@/lib/product-utils';
import { cn } from '@/lib/utils';
import { useProductGift } from '@/providers/product-gift';
import type { SCAPIShopperProductsSchemas } from 'src/types';
import ProductGiftDrawer from './product-gift-drawer';
import ProductGiftSummary from './product-gift-summary';

interface ProductGiftOptionProps {
    product: SCAPIShopperProductsSchemas['Product'];
}

export default function ProductGiftOption({ product }: ProductGiftOptionProps): ReactElement | null {
    const { t } = useTranslation('product');
    const { giftDetails, isDrawerOpen, saveGiftDetails, clearGiftDetails, openDrawer, closeDrawer } = useProductGift();
    const openTriggerRef = useRef<HTMLButtonElement>(null);
    const summaryEditRef = useRef<HTMLButtonElement>(null);
    const closeFocusTargetRef = useRef<'open-trigger' | 'summary-edit'>('open-trigger');

    const handleDrawerCloseAutoFocus = useCallback((event: Event) => {
        event.preventDefault();
        const target = closeFocusTargetRef.current;
        queueMicrotask(() => {
            if (target === 'summary-edit') {
                summaryEditRef.current?.focus();
                return;
            }

            openTriggerRef.current?.focus();
        });
    }, []);

    if (!supportsProductGiftOption(product)) {
        return null;
    }

    const content = getProductGiftContent(product, t);

    const handleCancel = () => {
        closeFocusTargetRef.current = giftDetails ? 'summary-edit' : 'open-trigger';

        if (!giftDetails) {
            clearGiftDetails();
            return;
        }

        closeDrawer();
    };

    const handleSave = (details: ProductGiftDetails) => {
        closeFocusTargetRef.current = 'summary-edit';
        saveGiftDetails(details);
    };

    const handleDrawerOpenChange = (open: boolean) => {
        if (open) {
            openDrawer();
            return;
        }

        handleCancel();
    };

    return (
        <>
            {giftDetails ? (
                <ProductGiftSummary
                    product={product}
                    giftDetails={giftDetails}
                    onEdit={openDrawer}
                    onRemove={clearGiftDetails}
                    editButtonRef={summaryEditRef}
                />
            ) : (
                <button
                    ref={openTriggerRef}
                    type="button"
                    data-testid="product-gift-option"
                    aria-haspopup="dialog"
                    aria-expanded={isDrawerOpen}
                    className={cn(
                        'flex w-full cursor-pointer items-center gap-3 rounded-md bg-neutral-white p-4 text-left',
                        focusVisibleOutline
                    )}
                    onClick={openDrawer}>
                    <span
                        aria-hidden
                        className="size-4 shrink-0 rounded-[2px] border border-[var(--field-text-color)] bg-transparent"
                    />
                    <span className="body-copy-m min-w-0 flex-1 text-neutral-10">{content.checkboxLabel}</span>
                </button>
            )}

            <ProductGiftDrawer
                product={product}
                open={isDrawerOpen}
                initialValues={giftDetails}
                onOpenChange={handleDrawerOpenChange}
                onSave={handleSave}
                onCancel={handleCancel}
                onCloseAutoFocus={handleDrawerCloseAutoFocus}
            />
        </>
    );
}
